import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  LayoutDashboard,
  Briefcase,
  Search,
  Bookmark,
  Unlock,
  User2,
  IndianRupee,
  CreditCard,
  Calendar,
  Edit,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  User,
} from "lucide-react";
import {
  fetchCompanyDetailsAsync,
  selectCompanyDetails,
} from "../../Company/CompanySingleSlice/CompanySingleSlice";
import { EMPLOYER_COMPANY_LOGO_SAVE_URL } from "../../common/URLs";

const Menu_Lg_Bar = () => {
  const location = useLocation();
  const [selectedLink, setSelectedLink] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // mobile open/close
  const [isCollapsed, setIsCollapsed] = useState(false); // desktop collapse
  const [isMobileView, setIsMobileView] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const companyDetails = useSelector(selectCompanyDetails);

  // Handle window resize and set mobile view state
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobileView(mobile);
      if (mobile) {
        // Ensure expanded on mobile (no collapse state)
        setIsCollapsed(false);
      }
    };

    handleResize(); // Initial run
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchCompanyDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      await dispatch(fetchCompanyDetailsAsync());
    } catch (err) {
      setError("Failed to fetch company details");
      console.error("Error fetching company details:", err);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchCompanyDetails();
  }, [fetchCompanyDetails]);

  const getActiveLinkFromPath = (pathname) => {
    if (pathname === "/employer-dashboard" || pathname === "/employer-dashboard/") {
      return "DashHome";
    } else if (pathname.includes("/manage-jobs")) {
      return "manage-jobs";
    } else if (pathname.includes("/search-candidate")) {
      return "search-candidate";
    } else if (pathname.includes("/save-candidate")) {
      return "save-candidate";
    } else if (pathname.includes("/unlocked-candidate")) {
      return "unlocked-candidate";
    } else if (pathname.includes("/candidate") && !pathname.includes("/save-candidate") && !pathname.includes("/unlocked-candidate")) {
      return "Candidate";
    } else if (pathname.includes("/subscription-plan")) {
      return "subscription-plan";
    } else if (pathname.includes("/subscription-billings")) {
      return "subscription-billings";
    } else if (pathname.includes("/schedule-interview")) {
      return "schedule-interview";
    } else if (pathname.includes("/edit-feedback")) {
      return "edit-feedback";
    } else if (pathname.includes("/support")) {
      return "support";
    }
    return "";
  };

  useEffect(() => {
    const activeLink = getActiveLinkFromPath(location.pathname);
    setSelectedLink(activeLink);
  }, [location.pathname]);

  const handleClick = (link, path) => {
    setSelectedLink(link);
    navigate(path);

    // Close sidebar on mobile after clicking a link
    if (isMobileView) {
      const evt = new CustomEvent("toggleSidebar", { detail: { isOpen: false } });
      document.dispatchEvent(evt);
      setIsSidebarOpen(false);
    }
  };

  // Receive toggle events from navbar
  useEffect(() => {
    const handleToggleEvent = (event) => {
      setIsSidebarOpen(event.detail.isOpen);
    };
    document.addEventListener("toggleSidebar", handleToggleEvent);
    return () => {
      document.removeEventListener("toggleSidebar", handleToggleEvent);
    };
  }, []);

  const toggleCollapse = () => {
    if (!isMobileView) {
      const next = !isCollapsed;
      setIsCollapsed(next);
      const event = new CustomEvent("sidebarCollapseChange", {
        detail: { isCollapsed: next },
      });
      document.dispatchEvent(event);
    }
  };

  const menuItems = [
    { key: "DashHome", path: "/employer-dashboard", icon: LayoutDashboard, text: "Employer Dashboard" },
    { key: "manage-jobs", path: "/employer-dashboard/manage-jobs", icon: Briefcase, text: "Manage Jobs" },
    { key: "search-candidate", path: "/employer-dashboard/search-candidate", icon: Search, text: "Search Candidates" },
    { key: "save-candidate", path: "/employer-dashboard/save-candidate", icon: Bookmark, text: "Saved Candidates" },
    { key: "unlocked-candidate", path: "/employer-dashboard/unlocked-candidate", icon: Unlock, text: "Unlocked Candidates" },
    { key: "Candidate", path: "/employer-dashboard/candidate", icon: User2, text: "Candidates" },
    // { key: "subscription-plan", path: "/employer-dashboard/subscription-plan", icon: IndianRupee, text: "Subscription" },
    // { key: "subscription-billings", path: "/employer-dashboard/subscription-billings", icon: CreditCard, text: "Billings" },
    { key: "schedule-interview", path: "/employer-dashboard/schedule-interview", icon: Calendar, text: "Schedule Interview" },
    { key: "edit-feedback", path: "/employer-dashboard/edit-feedback", icon: Edit, text: "Feedback" },
    { key: "support", path: "/employer-dashboard/support", icon: HelpCircle, text: "Support" },
  ];

  const profilePic = companyDetails?.company_logo;

  // Width classes based on collapse (desktop)
  const desktopWidth = isCollapsed && !isMobileView ? "lg:w-20" : "lg:w-64";

  return (
    <aside
      className={[
        "ed-sidebar",
        "fixed left-0 top-14 md:top-16 lg:top-0 bottom-0",
        "transform transition-transform duration-300 ease-in-out",
        isSidebarOpen ? "translate-x-0" : "-translate-x-full",
        "lg:translate-x-0",
        "bg-white border-r border-gray-200",
        "px-2 py-2 flex flex-col gap-2",
        "w-64", // base/mobile width
        desktopWidth, // desktop width
        "z-40",
        "overflow-y-auto",
      ].join(" ")}
      aria-hidden={!isSidebarOpen && isMobileView}
      role="navigation"
    >
      {/* User Profile / Collapse control */}
      <div className="flex items-center justify-between p-2 border-b border-gray-200 ">
        {/* Profile section - hide text on collapsed desktop, show on mobile */}
        {(!isCollapsed || isMobileView) && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center mb-2">
              {isLoading ? (
                <div className="w-8 h-8 rounded-full bg-gray-300 animate-pulse mr-2"></div>
              ) : profilePic ? (
                <img
                  src={`${EMPLOYER_COMPANY_LOGO_SAVE_URL}${profilePic}`}
                  alt="Company Logo"
                  className="w-8 h-8 rounded-full mr-2 object-cover"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center mr-2">
                  <User className="text-gray-600" size={16} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                {isLoading ? (
                  <>
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-1 animate-pulse"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                  </>
                ) : error ? (
                  <div className="text-xs text-red-500">Error loading profile</div>
                ) : (
                  <>
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {companyDetails?.contact_person_name || "No name"}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {companyDetails?.email || "No email"}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Collapse button (desktop only) */}
        {!isMobileView && (
          <div className="hidden lg:block ml-2">
            <button
              onClick={toggleCollapse}
              className="p-2 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {isCollapsed ? (
                <ChevronRight className="text-blue-700" size={24} />
              ) : (
                <ChevronLeft className="text-blue-700" size={24} />
              )}
            </button>
          </div>
        )}
      </div>

      {/* Menu Items */}
      <div className="flex-1 overflow-y-auto ">
        <div className="space-y-0">
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = selectedLink === item.key;
            return (
              <Link
                key={item.key}
                className={[
                  "rounded-lg flex items-center group transition-colors duration-200",
                  isCollapsed && !isMobileView ? "lg:justify-center lg:px-3 lg:py-3" : "px-3 py-3",
                  isActive ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100",
                ].join(" ")}
                to={item.path}
                onClick={() => handleClick(item.key, item.path)}
                title={isCollapsed && !isMobileView ? item.text : ""}
              >
                <div className="flex items-center w-full">
                  <IconComponent
                    className={[
                      isCollapsed && !isMobileView ? "mx-auto" : "",
                      "flex-shrink-0",
                      isActive ? "text-white" : "text-gray-500 group-hover:text-gray-700",
                    ].join(" ")}
                    size={20}
                  />
                  {(!isCollapsed || isMobileView) && (
                    <span className="text-sm whitespace-nowrap overflow-hidden ml-3">{item.text}</span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </aside>
  );
};

export default Menu_Lg_Bar;